/**
 * Request-error batching/reporting service.
 *
 * Responsibilities:
 * - watch queued request errors via a store adapter,
 * - flush on threshold (immediate) or periodic interval,
 * - send one batched payload to transports,
 * - retry failed blocking sends with bounded exponential backoff.
 */
import * as Sentry from '@sentry/vue';
import NewRelicBrowser from '@/plugins/newrelic-browser.ts';
import { readAuthContextAttributes } from '@/plugins/auth-context.ts';

type RequestErrorEntry = Record<string, unknown>;

type ReporterPayload = {
    attempt: number;
    errors: RequestErrorEntry[];
    reason: string;
    timestamp: string;
};

type ReporterTransport = {
    blocking: boolean;
    name: string;
    send: (payload: ReporterPayload) => Promise<void> | void;
};

type ReporterStoreAdapter = {
    acknowledgeBatch: (batch: RequestErrorEntry[]) => void;
    getErrors: () => RequestErrorEntry[];
};

type ReporterOptions = {
    intervalMs?: number;
    retryDelaysMs?: number[];
    store?: ReporterStoreAdapter;
    threshold?: number;
    transports?: ReporterTransport[];
};

const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;
const DEFAULT_THRESHOLD = 10;
const DEFAULT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

const createSentryTransport = (): ReporterTransport => ({
    name: 'sentry',
    // captureMessage() queues delivery inside the SDK and does not expose
    // network-transport failures to this caller, so it cannot participate
    // in the reporter's retry/backoff contract.
    blocking: false,
    send(payload) {
        Sentry.withScope((scope) => {
            scope.setExtras({
                infoOnTheError:
                    'This error is sent when 10 requests errors are logged or every 10 minutes if at least 1 request error was logged since the last reporting',
                attempt: payload.attempt,
                reason: payload.reason,
                timestamp: payload.timestamp,
                errors: payload.errors,
            });
            Sentry.captureMessage('Multiple errors occurred on requests', 'warning');
        });
    },
});

const createNewRelicTransport = (): ReporterTransport => ({
    name: 'new-relic',
    blocking: false,
    send(payload) {
        const firstError = payload.errors.at(0);

        NewRelicBrowser.addPageAction('request_error_batch_reported', {
            ...readAuthContextAttributes(),
            app_build_id: process.env.VUE_APP_BUILD_ID || 'unknown',
            app_version: process.env.VUE_APP_VERSION || 'unknown',
            count: payload.errors.length,
            firstErrorCode:
                typeof firstError?.code === 'string' || typeof firstError?.code === 'number'
                    ? String(firstError.code)
                    : undefined,
            reason: payload.reason,
            timestamp: payload.timestamp,
        });
    },
});

const createDefaultTransports = (): ReporterTransport[] => {
    return [createSentryTransport(), createNewRelicTransport()];
};

const toRequestErrorEntryList = (errors: unknown[]): RequestErrorEntry[] => {
    return errors.filter((error): error is RequestErrorEntry => {
        return Boolean(error && typeof error === 'object');
    });
};

const removeBatchFromQueue = <T>(queue: T[], batch: T[]): T[] => {
    const queueCopy = [...queue];
    const counts = new Map<T, number>();

    for (const item of batch) {
        counts.set(item, (counts.get(item) ?? 0) + 1);
    }

    return queueCopy.filter((item) => {
        const count = counts.get(item) ?? 0;

        if (count <= 0) {
            return true;
        }

        counts.set(item, count - 1);
        return false;
    });
};

class RequestErrorReporter {
    private intervalId: ReturnType<typeof setInterval> | null = null;

    private isFlushing = false;

    private hasPendingThresholdFlush = false;

    private lastFailureReason: string | null = null;

    private shouldBeRunning = false;

    private readonly retryDelaysMs: number[];

    private retryAttempt = 0;

    private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private readonly intervalMs: number;

    private store: ReporterStoreAdapter | null = null;

    private readonly threshold: number;

    private readonly transports: ReporterTransport[];

    constructor(options: ReporterOptions = {}) {
        this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
        this.retryDelaysMs = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
        this.store = options.store ?? null;
        this.threshold = options.threshold ?? DEFAULT_THRESHOLD;
        this.transports = options.transports ?? createDefaultTransports();
    }

    configure(options: Pick<ReporterOptions, 'store'>): void {
        if (options.store) {
            this.store = options.store;
        }

        this.startSchedulerIfReady();
    }

    getDebugState() {
        return {
            hasStore: Boolean(this.store),
            hasRetryScheduled: Boolean(this.retryTimeoutId),
            intervalMs: this.intervalMs,
            isFlushing: this.isFlushing,
            isRunning: Boolean(this.intervalId),
            lastFailureReason: this.lastFailureReason,
            retryAttempt: this.retryAttempt,
            shouldBeRunning: this.shouldBeRunning,
            threshold: this.threshold,
        };
    }

    start(): void {
        this.shouldBeRunning = true;
        this.startSchedulerIfReady();
    }

    stop(): void {
        this.shouldBeRunning = false;

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = null;
        }

        this.hasPendingThresholdFlush = false;
        this.retryAttempt = 0;
        this.lastFailureReason = null;
    }

    onErrorsChanged(count: number): void {
        if (count < this.threshold) {
            return;
        }

        if (this.isFlushing) {
            this.hasPendingThresholdFlush = true;
            return;
        }

        this.flushNow('threshold').catch(() => {
            // noop - flushNow already manages retries and state
        });
    }

    async flushNow(reason = 'manual'): Promise<boolean> {
        if (!this.store || this.isFlushing) {
            return false;
        }

        const errors = toRequestErrorEntryList(this.store.getErrors());

        if (!errors.length) {
            this.resetRetryState();
            return true;
        }

        this.isFlushing = true;

        const payload: ReporterPayload = {
            attempt: this.retryAttempt + 1,
            errors: [...errors],
            reason,
            timestamp: new Date().toISOString(),
        };

        let blockingTransportFailed = false;

        try {
            const transportResults = await Promise.all(
                this.transports.map(async (transport) => {
                    try {
                        await transport.send(payload);
                        return {
                            failed: false,
                            transport,
                        };
                    } catch {
                        return {
                            failed: true,
                            transport,
                        };
                    }
                })
            );

            blockingTransportFailed = transportResults.some((result) => {
                return result.failed && result.transport.blocking;
            });

            if (blockingTransportFailed) {
                this.lastFailureReason = reason;
                this.scheduleRetry();
                return false;
            }

            this.store.acknowledgeBatch(payload.errors);
            this.resetRetryState();
            return true;
        } finally {
            this.isFlushing = false;

            if (this.hasPendingThresholdFlush && !this.retryTimeoutId) {
                this.hasPendingThresholdFlush = false;
                this.flushNow('threshold-pending').catch(() => {
                    // noop - flushNow already manages retries and state
                });
            }
        }
    }

    private scheduleRetry(): void {
        if (this.retryTimeoutId || this.retryAttempt >= this.retryDelaysMs.length) {
            return;
        }

        const delay = this.retryDelaysMs[this.retryAttempt];
        this.retryAttempt += 1;

        this.retryTimeoutId = setTimeout(() => {
            this.retryTimeoutId = null;
            this.flushNow('retry').catch(() => {
                // noop - flushNow already manages retries and state
            });
        }, delay);
    }

    private resetRetryState(): void {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = null;
        }

        this.retryAttempt = 0;
        this.lastFailureReason = null;
    }

    private startSchedulerIfReady(): void {
        if (!this.shouldBeRunning || !this.store || this.intervalId) {
            return;
        }

        this.intervalId = setInterval(() => {
            if ((this.store?.getErrors()?.length ?? 0) < 1) {
                return;
            }

            this.flushNow('periodic').catch(() => {
                // noop - flushNow already manages retries and state
            });
        }, this.intervalMs);
    }
}

const requestErrorReporter = new RequestErrorReporter();

export { removeBatchFromQueue };
export type { ReporterStoreAdapter, ReporterTransport, RequestErrorEntry };
export {
    RequestErrorReporter,
    createDefaultTransports,
    createNewRelicTransport,
    createSentryTransport,
};
export default requestErrorReporter;
